import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Home, MapPin, Calendar, Sofa, Wifi, UtensilsCrossed, 
  Tv, Shirt, ShowerHead, CheckCircle, ArrowRight, Phone, Sparkles, Heart, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const propertyTypes = [
  { 
    type: '1BHK', 
    title: 'Cozy 1BHK', 
    price: '25,000',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=600',
    features: ['Perfect for singles', 'Compact & efficient', 'Great for short stays']
  },
  { 
    type: '2BHK', 
    title: 'Spacious 2BHK', 
    price: '40,000',
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=600',
    features: ['Ideal for couples', 'Home office space', 'Guest room available']
  },
  { 
    type: '3BHK', 
    title: 'Family 3BHK', 
    price: '60,000',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600',
    features: ['Perfect for families', 'Multiple bathrooms', 'Premium amenities']
  },
];

const amenities = [
  { icon: Sofa, label: 'Fully Furnished' },
  { icon: Wifi, label: 'High-Speed WiFi' },
  { icon: UtensilsCrossed, label: 'Modular Kitchen' },
  { icon: Tv, label: 'Smart TV' },
  { icon: Shirt, label: 'Washing Machine' },
  { icon: ShowerHead, label: 'Geyser & AC' },
];

const zenPromises = [
  'Move-in ready with zero hassle',
  'All utilities included in rent',
  'Monthly housekeeping',
  'Responsive maintenance support',
  'Flexible lease terms',
  'No brokerage fees',
];

const IndividualRentals = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState('2BHK');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: 'Hyderabad',
    property_type: '2BHK',
    move_in_date: '',
    duration: '6-months',
    budget: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_line: 'individual_rentals',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          source: 'website',
          metadata: {
            property_type: formData.property_type,
            move_in_date: formData.move_in_date,
            duration: formData.duration,
            budget: formData.budget
          }
        })
      });
      if (response.ok) {
        setSubmitted(true);
        toast.success('Inquiry submitted! Our team will contact you within 24-48 hours.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="individual-rentals-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/10 via-white to-amber-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Home className="w-4 h-4" /> Zen Living
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Live in a <span className="text-primary">Fully Furnished</span> Zen Property
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Experience hassle-free living in our carefully curated, move-in ready apartments. Just bring your bags – we have got everything else covered.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary text-white px-8"
                  onClick={() => document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  View Properties <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="talk-to-us-btn"
                >
                  <Phone className="w-5 h-5 mr-2" /> Talk to Us
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img loading="lazy" decoding="async" 
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200"
                  alt="Zen Living Space"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-lg">200+</p>
                  <p className="text-gray-500 text-sm">Happy Residents</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-2">Every Zen Property Includes</h2>
            <p className="text-gray-600">Premium amenities for comfortable living</p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {amenities.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white px-6 py-4 rounded-full shadow-sm">
                <item.icon className="w-5 h-5 text-primary" />
                <span className="font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section className="py-20" id="properties">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Zen Space</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From cozy studios to spacious family apartments, find the perfect space that fits your lifestyle.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {propertyTypes.map((property) => (
              <div 
                key={property.type}
                className={`bg-white rounded-2xl overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  selectedType === property.type 
                    ? 'border-primary shadow-xl scale-105' 
                    : 'border-gray-100 hover:border-primary/50 hover:shadow-lg'
                }`}
                onClick={() => {
                  setSelectedType(property.type);
                  setFormData({...formData, property_type: property.type});
                }}
              >
                <div className="aspect-video relative">
                  <img loading="lazy" decoding="async" 
                    src={property.image} 
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                  {selectedType === property.type && (
                    <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                      Selected
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{property.title}</h3>
                      <p className="text-gray-500 text-sm">{property.type} Apartment</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">₹{property.price}</p>
                      <p className="text-gray-500 text-xs">/month</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {property.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-primary mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zen Promise + Form */}
      <section className="py-20 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Zen Promise */}
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" /> The Zen Promise
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">More Than Just a Rental</h2>
              <p className="text-gray-600 mb-8">
                When you rent a Zen property, you are not just getting a place to stay – you are getting a lifestyle upgrade. We handle all the complexities so you can focus on living your best life.
              </p>
              <div className="space-y-4">
                {zenPromises.map((promise, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="font-medium">{promise}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div id="inquiry-form" className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold mb-2">Schedule a Viewing</h3>
              <p className="text-gray-600 mb-6">Fill in your details and we will arrange a property tour.</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="you@email.com"
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Select value={formData.city} onValueChange={(v) => setFormData({...formData, city: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                        <SelectItem value="Bangalore">Bangalore</SelectItem>
                        <SelectItem value="Mumbai">Mumbai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <Select value={formData.property_type} onValueChange={(v) => setFormData({...formData, property_type: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1BHK">1BHK</SelectItem>
                        <SelectItem value="2BHK">2BHK</SelectItem>
                        <SelectItem value="3BHK">3BHK</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Preferred Move-in Date</Label>
                    <Input
                      type="date"
                      value={formData.move_in_date}
                      onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                      className="rounded-xl mt-1"
                    />
                  </div>
                  <div>
                    <Label>Lease Duration</Label>
                    <Select value={formData.duration} onValueChange={(v) => setFormData({...formData, duration: v})}>
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3-months">3 Months</SelectItem>
                        <SelectItem value="6-months">6 Months</SelectItem>
                        <SelectItem value="11-months">11 Months</SelectItem>
                        <SelectItem value="1-year">1 Year+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full rounded-full bg-primary py-6" data-testid="individual-submit-btn">
                  Schedule Viewing <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndividualRentals;
